import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { TourCard, ActivityCard, BlogCard, ReviewCard } from '../components/ui/Cards';
import { tours, activities, destinations, blogPosts, testimonials } from '../data';
import './Home.css';

function Hero() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [form, setForm] = useState({ type: '', destination: '', date: '', travelers: '2' });

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (form.destination) params.set('destination', form.destination);
    if (form.type === 'activities') {
      navigate(`/activities?${params.toString()}`);
    } else {
      if (form.type) params.set('category', form.type);
      navigate(`/tours?${params.toString()}`);
    }
  };

  return (
    <section className="hero">
      <div className="hero__bg-img">
        <img
          src="https://www.image2url.com/r2/default/images/1776815527025-dafa3e62-effe-414e-9ce3-b27e7f6ed83d.jpg"
          alt="Morocco Atlas Mountains"
        />
      </div>
      <div className="hero__overlay" />

      <div className="hero__inner container">
        <div className="hero__content">
          <div className="hero__tag">{t('hero.tag')}</div>
          <h1 className="hero__h1">{t('hero.title1')} <em>{t('hero.title2')}</em><br />{t('hero.title3')}</h1>
          <p className="hero__sub">{t('hero.sub')}</p>
          <div className="hero__btns">
            <Link to="/tours" className="btn btn-primary">{t('hero.exploreTours')}</Link>
            <Link to="/activities" className="btn btn-outline">{t('hero.viewActivities')}</Link>
          </div>
          <div className="hero__trust">
            {[t('hero.trust1'), t('hero.trust2'), t('hero.trust3'), t('hero.trust4')].map(item => (
              <div key={item} className="hero__trust-item">
                <span className="hero__trust-dot" />
                {item}
              </div>
            ))}
          </div>
        </div>

        <form className="hero__search" onSubmit={handleSearch}>
          <div className="hero__search-title">{t('search.title')}</div>

          <div className="hero__search-field">
            <label>{t('search.tourType')}</label>
            <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}>
              <option value="">{t('search.allTypes')}</option>
              <option value="city-tours">{t('search.cityTours')}</option>
              <option value="day-trips">{t('search.dayTrips')}</option>
              <option value="activities">{t('search.activities')}</option>
            </select>
          </div>

          <div className="hero__search-field">
            <label>{t('search.destination')}</label>
            <select value={form.destination} onChange={e => setForm({ ...form, destination: e.target.value })}>
              <option value="">{t('search.allDestinations')}</option>
              {destinations.map(d => <option key={d.slug} value={d.slug}>{d.name}</option>)}
            </select>
          </div>

          <div className="hero__search-field">
            <label>{t('search.date')}</label>
            <input
              type="date"
              value={form.date}
              onChange={e => setForm({ ...form, date: e.target.value })}
              min={new Date().toISOString().split('T')[0]}
            />
          </div>

          <div className="hero__search-field">
            <label>{t('search.travelers')}</label>
            <select value={form.travelers} onChange={e => setForm({ ...form, travelers: e.target.value })}>
              <option value="1">{t('search.1person')}</option>
              <option value="2">{t('search.2people')}</option>
              <option value="3">{t('search.35people')}</option>
              <option value="6">{t('search.6people')}</option>
            </select>
          </div>

          <button type="submit" className="btn btn-primary hero__search-btn">
            {t('search.search')}
          </button>
        </form>
      </div>
    </section>
  );
}

function FeaturedTours() {
  const { t } = useTranslation();
  const featured = tours.filter(tr => tr.featured).slice(0, 4);
  return (
    <section className="section section-white">
      <div className="container">
        <div className="section-header">
          <span className="section-tag">{t('home.featuredTag')}</span>
          <h2 className="section-title">{t('home.featuredTitle')}</h2>
          <p className="section-subtitle">{t('home.featuredSub')}</p>
        </div>
        <div className="grid-4">
          {featured.map(tr => <TourCard key={tr.id} tour={tr} />)}
        </div>
        <Link to="/tours" className="view-all-link">{t('home.viewAllTours')}</Link>
      </div>
    </section>
  );
}

function PopularActivities() {
  const { t } = useTranslation();
  const featured = activities.filter(a => a.featured).slice(0, 6);
  return (
    <section className="section section-alt">
      <div className="container">
        <div className="section-header">
          <span className="section-tag">{t('home.activitiesTag')}</span>
          <h2 className="section-title">{t('home.activitiesTitle')}</h2>
          <p className="section-subtitle">{t('home.activitiesSub')}</p>
        </div>
        <div className="grid-3">
          {featured.map(a => <ActivityCard key={a.id} activity={a} />)}
        </div>
        <Link to="/activities" className="view-all-link">{t('home.viewAllActivities')}</Link>
      </div>
    </section>
  );
}

const destImagesHome = {
  'marrakech':       'https://images.unsplash.com/photo-1670406071577-d795729196b4?fm=jpg&q=60&w=3000&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3Dhttps://images.unsplash.com/photo-1597212618440-806262de4f3b?w=800&q=80',
  'atlas-mountains': 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800&q=80',
  'agafay-desert':   'https://images.unsplash.com/photo-1509316785289-025f5b846b35?w=800&q=80',
  'essaouira':       'https://images.unsplash.com/photo-1624802704371-c76faa34c3a0?fm=jpg&q=60&w=3000&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
  'ouzoud':          'https://www.image2url.com/r2/default/images/1776889330860-491f6402-d23c-4714-80b8-2b5485c43d64.jpg',
  'ait-ben-haddou':  'https://images.unsplash.com/photo-1737927427538-9f9b4ea4ab02?fm=jpg&q=60&w=3000&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
  'ourika-valley':   'https://images.unsplash.com/photo-1548013146-72479768bada?w=800&q=80',
  'casablanca':      'https://images.unsplash.com/photo-1553913861-c0fddf2619ee?w=800&q=80',
};

function DestinationsSection() {
  const { t } = useTranslation();
  const top = destinations.slice(0, 6);
  return (
    <section className="section section-white">
      <div className="container">
        <div className="section-header">
          <span className="section-tag">{t('home.destTag')}</span>
          <h2 className="section-title">{t('home.destTitle')}</h2>
          <p className="section-subtitle">{t('home.destSub')}</p>
        </div>
        <div className="dest-home-grid">
          <Link to={`/destinations/${top[0].slug}`} className="dest-home-card dest-home-card--large">
            <img src={destImagesHome[top[0].slug]} alt={top[0].name} loading="lazy" />
            <div className="dest-home-card__overlay" />
            <div className="dest-home-card__body">
              <h3 className="dest-home-card__name">{top[0].name}</h3>
              <p className="dest-home-card__tagline">{top[0].tagline}</p>
              <div className="dest-home-card__counts">
                {top[0].tourCount > 0 && <span>{top[0].tourCount} {t('destinations.tours')}</span>}
                {top[0].activityCount > 0 && <span>{top[0].activityCount} {t('destinations.activities')}</span>}
              </div>
            </div>
          </Link>
          <div className="dest-home-grid__right">
            {top.slice(1).map(d => (
              <Link key={d.id} to={`/destinations/${d.slug}`} className="dest-home-card">
                <img src={destImagesHome[d.slug]} alt={d.name} loading="lazy" />
                <div className="dest-home-card__overlay" />
                <div className="dest-home-card__body">
                  <h3 className="dest-home-card__name">{d.name}</h3>
                  <p className="dest-home-card__tagline">{d.tagline}</p>
                  <div className="dest-home-card__counts">
                    {d.tourCount > 0 && <span>{d.tourCount} {t('destinations.tours')}</span>}
                    {d.activityCount > 0 && <span>{d.activityCount} {t('destinations.activities')}</span>}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
        <Link to="/destinations" className="view-all-link">{t('home.viewAllDest')}</Link>
      </div>
    </section>
  );
}

const whyIcons = {
  guide: <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76"/></svg>,
  secure: <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><polyline points="9 12 11 14 15 10"/></svg>,
  whatsapp: <svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347zM12 0C5.373 0 0 5.373 0 12c0 2.123.553 4.116 1.522 5.852L0 24l6.313-1.492A11.954 11.954 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818a9.818 9.818 0 01-5.006-1.37l-.36-.213-3.736.882.935-3.626-.234-.372A9.818 9.818 0 1112 21.818z"/></svg>,
  cancel: <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/><line x1="8" y1="12" x2="16" y2="12" strokeWidth="0"/></svg>,
  pickup: <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="3" width="15" height="13" rx="2"/><path d="M16 8h4l3 5v3h-7V8z"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>,
  star: <svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>,
};

function WhyUs() {
  const { t } = useTranslation();
  const usp = [
    { icon: whyIcons.guide,    title: t('whyUs.guide1'),    sub: t('whyUs.guide1sub') },
    { icon: whyIcons.secure,   title: t('whyUs.secure'),    sub: t('whyUs.secureSub') },
    { icon: whyIcons.whatsapp, title: t('whyUs.whatsapp'),  sub: t('whyUs.whatsappSub') },
    { icon: whyIcons.cancel,   title: t('whyUs.cancel'),    sub: t('whyUs.cancelSub') },
    { icon: whyIcons.pickup,   title: t('whyUs.pickup'),    sub: t('whyUs.pickupSub') },
    { icon: whyIcons.star,     title: t('whyUs.rating'),    sub: t('whyUs.ratingSub') },
  ];
  return (
    <section className="section section-dark">
      <div className="container">
        <div className="section-header">
          <span className="section-tag">{t('home.whyTag')}</span>
          <h2 className="section-title" style={{ color: '#fff' }}>{t('home.whyTitle')}</h2>
          <p className="section-subtitle" style={{ color: 'rgba(255,255,255,0.55)' }}>{t('home.whySub')}</p>
        </div>
        <div className="why-grid">
          {usp.map(u => (
            <div key={u.title} className="why-card">
              <div className="why-card__icon">{u.icon}</div>
              <h3 className="why-card__title">{u.title}</h3>
              <p className="why-card__sub">{u.sub}</p>
            </div>
          ))}
        </div>
        <div className="stats-row">
          {[['5,000+', 'Happy travelers'], ['12', 'Years of experience'], ['23', 'Unique tours & activities'], ['4.9/5', 'Average rating']].map(([n, l]) => (
            <div key={l} className="stat">
              <div className="stat__num">{n}</div>
              <div className="stat__label">{l}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowItWorks() {
  const { t } = useTranslation();
  const steps = [
    { n: '1', title: t('steps.step1Title'), desc: t('steps.step1Desc') },
    { n: '2', title: t('steps.step2Title'), desc: t('steps.step2Desc') },
    { n: '3', title: t('steps.step3Title'), desc: t('steps.step3Desc') },
    { n: '4', title: t('steps.step4Title'), desc: t('steps.step4Desc') },
  ];
  return (
    <section className="section section-alt">
      <div className="container">
        <div className="section-header">
          <span className="section-tag">{t('home.howTag')}</span>
          <h2 className="section-title">{t('home.howTitle')}</h2>
        </div>
        <div className="steps-row">
          {steps.map((s) => (
            <div key={s.n} className="step">
              <div className="step__num">{s.n}</div>
              <h3 className="step__title">{s.title}</h3>
              <p className="step__desc">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Reviews() {
  const { t } = useTranslation();
  return (
    <section className="section section-white">
      <div className="container">
        <div className="section-header">
          <span className="section-tag">{t('home.reviewsTag')}</span>
          <h2 className="section-title">{t('home.reviewsTitle')}</h2>
          <p className="section-subtitle">{t('home.reviewsSub')}</p>
        </div>
        <div className="grid-3">
          {testimonials.slice(0, 3).map(r => <ReviewCard key={r.id} review={r} />)}
        </div>
        <Link to="/testimonials" className="view-all-link">{t('home.readAllReviews')}</Link>
      </div>
    </section>
  );
}

function BlogPreview() {
  const { t } = useTranslation();
  return (
    <section className="section section-alt">
      <div className="container">
        <div className="section-header">
          <span className="section-tag">{t('home.blogTag')}</span>
          <h2 className="section-title">{t('home.blogTitle')}</h2>
          <p className="section-subtitle">{t('home.blogSub')}</p>
        </div>
        <div className="grid-3">
          {blogPosts.slice(0, 3).map(p => <BlogCard key={p.id} post={p} />)}
        </div>
        <Link to="/morocco-guide" className="view-all-link">{t('home.exploreMorocco')}</Link>
      </div>
    </section>
  );
}

function Newsletter() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const handleSubmit = (e) => { e.preventDefault(); if (email) setSent(true); };
  return (
    <section className="newsletter">
      <div className="container newsletter__inner">
        <div>
          <div className="section-tag" style={{ color: 'rgba(255,255,255,0.8)' }}>Free Guide</div>
          <h2 className="newsletter__title">Get Our Free Morocco <em>Travel Guide</em></h2>
          <p className="newsletter__sub">Tips, itineraries & exclusive offers — no spam, unsubscribe anytime.</p>
        </div>
        {sent ? (
          <div className="newsletter__thanks">✓ Thank you! Check your inbox for the guide.</div>
        ) : (
          <form className="newsletter__form" onSubmit={handleSubmit}>
            <input type="email" placeholder="Your email address" value={email} onChange={e => setEmail(e.target.value)} required className="newsletter__input" />
            <button type="submit" className="btn btn-white">Get Free Guide</button>
          </form>
        )}
      </div>
    </section>
  );
}

function WaBanner() {
  return (
    <section className="wa-banner">
      <div className="container wa-banner__inner">
        <div className="wa-banner__icon">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="white">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
            <path d="M12 0C5.373 0 0 5.373 0 12c0 2.123.553 4.116 1.522 5.852L0 24l6.313-1.492A11.954 11.954 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818a9.818 9.818 0 01-5.006-1.37l-.36-.213-3.736.882.935-3.626-.234-.372A9.818 9.818 0 1112 21.818z"/>
          </svg>
        </div>
        <div>
          <h3 className="wa-banner__title">Have questions? Chat with us now</h3>
          <p className="wa-banner__sub">We reply within minutes — 7 days a week</p>
        </div>
        <a href="https://wa.me/212694401220" target="_blank" rel="noreferrer" className="btn" style={{ background: '#25d366', color: '#fff' }}>
          Open WhatsApp
        </a>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <>
      <Hero />
      <FeaturedTours />
      <PopularActivities />
      <DestinationsSection />
      <WhyUs />
      <HowItWorks />
      <Reviews />
      <BlogPreview />
      <Newsletter />
      <WaBanner />
    </>
  );
}
